import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import EstadoBadge from "../components/EstadoBadge";
import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Globe2, Building2, Tags, Plus, Trash2, Infinity as InfinityIcon, RefreshCw, AlertTriangle, X, RotateCcw } from "lucide-react";
import { ACCION_TIPO_LABEL, DIRECCION_LABEL, VIGENCIA_LABEL } from "../lib/constants";
import AccionForm from "../components/AccionForm";

export default function ConvenioDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { config } = useConfig();
  const nav = useNavigate();
  const [convenio, setConvenio] = useState(null);
  const [acciones, setAcciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAccionForm, setShowAccionForm] = useState(false);
  const [showNoRenovModal, setShowNoRenovModal] = useState(false);

  const load = () => {
    Promise.all([
      api.get(`/convenios/${id}`),
      api.get(`/acciones`, { params: { convenio_id: id } }),
    ])
      .then(([c, a]) => {
        setConvenio(c.data);
        setAcciones(a.data);
      })
      .catch(() => toast.error(t("cd_x.load_err")))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const onAccionCreated = () => {
    setShowAccionForm(false);
    load();
    toast.success(t("cd_x.toast_action"));
  };

  const deleteConvenio = async () => {
    if (!confirm(t("cd_x.confirm_delete"))) return;
    try {
      await api.delete(`/convenios/${id}`);
      toast.success(t("cd_x.toast_deleted"));
      nav("/convenios");
    } catch {
      toast.error(t("toast.error_delete"));
    }
  };

  const cancelarNoRenovacion = async () => {
    if (!confirm(t("cd_x.confirm_cancel_nr"))) return;
    try {
      const { data } = await api.post(`/convenios/${id}/cancelar-no-renovacion`);
      setConvenio(data);
      toast.success(t("cd_x.toast_cancel_nr"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_generic"));
    }
  };

  if (loading) return <Layout><div className="p-16 text-zinc-500">{t("cd_x.loading")}</div></Layout>;
  if (!convenio) return <Layout><div className="p-16">{t("cd_x.not_found")}</div></Layout>;

  const canRegister = user?.role !== "directivo";

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <Link to="/convenios" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6 sm:mb-8" data-testid="back-link">
          <ArrowLeft size={14} strokeWidth={1.5} /> {t("cd_x.back")}
        </Link>

        {/* Header */}
        <div className="pb-6 sm:pb-8 mb-8 sm:mb-12 border-b border-zinc-200">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
            <div className="min-w-0">
              <div className="section-marker">{t("convenios.page_title")} · {convenio.tipo}</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter mt-2 break-words">
                {convenio.institucion_nombre}
              </h1>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-4 text-zinc-600">
                <span className="inline-flex items-center gap-2 text-sm sm:text-base"><Globe2 size={16} strokeWidth={1.5} /> {convenio.institucion_pais}{convenio.institucion_ciudad && ` · ${convenio.institucion_ciudad}`}</span>
                <EstadoBadge estado={convenio.estado} size="lg" />
              </div>
            </div>
            {canRegister && (
              <button
                onClick={() => setShowAccionForm(true)}
                className="bg-zinc-950 text-white px-5 py-3 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 transition-colors shrink-0 w-full sm:w-auto"
                data-testid="btn-register-accion"
              >
                <Plus size={18} strokeWidth={1.5} /> {t("convenio_detail.btn_register_action")}
              </button>
            )}
          </div>
        </div>

        {/* Two-column detail + history */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
          {/* Left: details */}
          <div className="lg:col-span-1 space-y-8">
            {/* Vigencia */}
            <div>
              <div className="section-marker mb-3 flex items-center gap-2">
                {convenio.vigencia_tipo === "indefinido" ? <InfinityIcon size={13} strokeWidth={1.5} /> : convenio.vigencia_tipo === "auto_renovable" ? <RefreshCw size={13} strokeWidth={1.5} /> : <Calendar size={13} strokeWidth={1.5} />}
                {t("convenio_detail.section_vigencia")}
              </div>
              <div className="text-xl font-medium">{VIGENCIA_LABEL[convenio.vigencia_tipo || "fijo"]}</div>
              <div className="mt-4 text-sm text-zinc-500">{t("cd_x.firma")}</div>
              <div className="text-lg tabular-nums">{convenio.fecha_firma}</div>

              {convenio.vigencia_tipo !== "indefinido" && (
                <>
                  <div className="text-sm text-zinc-500 mt-4">
                    {convenio.vigencia_tipo === "auto_renovable" ? t("cd_x.fecha_1er") : t("cd_x.vencimiento")}
                  </div>
                  <div className="text-lg tabular-nums">{convenio.fecha_vencimiento}</div>
                </>
              )}

              {convenio.vigencia_tipo === "auto_renovable" && (
                <div className="mt-3 text-xs text-zinc-500">
                  {t("cd_x.auto_text")} <strong>{convenio.aviso_previo_dias} {t("cd_x.days_lower", { defaultValue: t("convenio_detail.days") })}</strong>.
                </div>
              )}

              {convenio.no_renovacion_iniciada && (
                <div className="mt-4 border-l-2 border-amber-500 pl-4 py-2 bg-amber-50">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1">
                    <AlertTriangle size={12} strokeWidth={1.5} /> {t("cd_x.nr_kicker")}
                  </div>
                  <div className="text-sm mt-2">
                    {t("cd_x.nr_given")} <strong>{convenio.no_renovacion_fecha_aviso}</strong>.
                  </div>
                  <div className="text-sm">
                    {t("cd_x.nr_ends")} <strong>{convenio.no_renovacion_fecha_efectiva}</strong>.
                  </div>
                  {convenio.no_renovacion_motivo && (
                    <div className="text-xs text-zinc-600 mt-2">{convenio.no_renovacion_motivo}</div>
                  )}
                  {user?.role === "admin" && (
                    <button onClick={cancelarNoRenovacion} className="mt-3 text-xs underline inline-flex items-center gap-1" data-testid="btn-cancelar-no-renov">
                      <RotateCcw size={12} strokeWidth={1.5} /> {t("cd_x.nr_cancel")}
                    </button>
                  )}
                </div>
              )}

              {user?.role === "admin" &&
                convenio.vigencia_tipo === "auto_renovable" &&
                !convenio.no_renovacion_iniciada && (
                  <button
                    onClick={() => setShowNoRenovModal(true)}
                    className="mt-4 w-full border border-amber-500 text-amber-700 px-4 py-2 text-sm font-medium hover:bg-amber-50 inline-flex items-center justify-center gap-2"
                    data-testid="btn-iniciar-no-renov"
                  >
                    <AlertTriangle size={14} strokeWidth={1.5} />
                    {t("cd_x.nr_start")}
                  </button>
                )}
            </div>

            <div>
              <div className="section-marker mb-3 flex items-center gap-2"><Building2 size={13} strokeWidth={1.5} /> {config.facultad_label_plural || t("nav.facultades")}</div>
              <div className="space-y-1">
                {(convenio.facultad_nombres || []).length === 0 && <span className="text-zinc-500 text-sm">—</span>}
                {(convenio.facultad_nombres || []).map((f) => (
                  <div key={f} className="text-sm">{f}</div>
                ))}
              </div>
            </div>

            {config.enable_unidades && (convenio.unidad_nombres || []).length > 0 && (
              <div>
                <div className="section-marker mb-3 flex items-center gap-2"><Building2 size={13} strokeWidth={1.5} /> {config.unidad_label_plural || t("nav.unidades")}</div>
                <div className="space-y-1">
                  {convenio.unidad_nombres.map((u) => (
                    <div key={u} className="text-sm">{u}</div>
                  ))}
                </div>
              </div>
            )}

            {(convenio.estructura_nombres || []).length > 0 && (
              <div>
                <div className="section-marker mb-3 flex items-center gap-2"><Building2 size={13} strokeWidth={1.5} /> {t("cd_x.other_struct")}</div>
                <div className="space-y-1">
                  {convenio.estructura_nombres.map((e) => (
                    <div key={e} className="text-sm">{e}</div>
                  ))}
                </div>
              </div>
            )}

            {(convenio.areas_tematicas || []).length > 0 && (
              <div>
                <div className="section-marker mb-3 flex items-center gap-2"><Tags size={13} strokeWidth={1.5} /> {t("cd_x.areas_tema")}</div>
                <div className="flex flex-wrap gap-2">
                  {convenio.areas_tematicas.map((a) => (
                    <span key={a} className="border border-zinc-200 px-2.5 py-1 text-xs">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {convenio.observaciones && (
              <div>
                <div className="section-marker mb-3">{t("cn.observaciones")}</div>
                <p className="text-sm text-zinc-600 leading-relaxed">{convenio.observaciones}</p>
              </div>
            )}

            {user?.role === "admin" && (
              <button onClick={deleteConvenio} className="inline-flex items-center gap-2 text-sm text-red-700 hover:underline" data-testid="btn-delete">
                <Trash2 size={14} strokeWidth={1.5} /> {t("cd_x.delete_btn")}
              </button>
            )}
          </div>

          {/* Right: action history timeline */}
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="section-marker">{t("cd_x.historial")}</div>
                <h2 className="text-3xl font-medium tracking-tight mt-2">
                  {acciones.length} <span className="serif-em">{t("cd_x.acciones_em")}</span>
                </h2>
              </div>
            </div>

            {acciones.length === 0 ? (
              <div className="border border-zinc-200 p-10 text-center">
                <div className="text-zinc-400 mb-2">◇</div>
                <p className="text-zinc-500">{t("cd_x.empty")}</p>
                {canRegister && (
                  <button onClick={() => setShowAccionForm(true)} className="mt-4 underline text-sm" data-testid="empty-register">{t("cd_x.register_first")}</button>
                )}
              </div>
            ) : (
              <div className="border-l-2 border-zinc-200 pl-6 space-y-6">
                {acciones.map((a) => (
                  <div key={a.id} className="relative" data-testid={`accion-${a.id}`}>
                    <span className="absolute -left-[29px] top-2 w-3 h-3 rounded-full bg-zinc-950"></span>
                    <div className="border border-zinc-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-medium">{ACCION_TIPO_LABEL[a.tipo] || a.tipo}</div>
                          {a.tipo_otro_descripcion && <div className="text-sm text-zinc-500 mt-1">{a.tipo_otro_descripcion}</div>}
                          <div className="text-xs text-zinc-500 mt-2">
                            {a.facultad_nombre} · {t("cd_x.semestre")} {a.semestre}/{a.anio}
                            {a.direccion !== "n_a" && ` · ${DIRECCION_LABEL[a.direccion]}`}
                          </div>
                          {a.observaciones && <p className="text-sm text-zinc-600 mt-3">{a.observaciones}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="kpi-number text-3xl">{a.num_participantes}</div>
                          <div className="section-marker">{t("convenio_detail.participantes")}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {showAccionForm && (
          <AccionForm
            convenio={convenio}
            onClose={() => setShowAccionForm(false)}
            onCreated={onAccionCreated}
          />
        )}

        {showNoRenovModal && (
          <NoRenovacionModal
            convenio={convenio}
            onClose={() => setShowNoRenovModal(false)}
            onDone={(updated) => { setConvenio(updated); setShowNoRenovModal(false); }}
          />
        )}
      </div>
    </Layout>
  );
}

function NoRenovacionModal({ convenio, onClose, onDone }) {
  const { t } = useTranslation();
  const [fechaEfectiva, setFechaEfectiva] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/convenios/${convenio.id}/iniciar-no-renovacion`, {
        fecha_efectiva: fechaEfectiva,
        motivo: motivo || null,
      });
      toast.success(t("cd_x.nr_modal_started"));
      onDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!fechaEfectiva && convenio.aviso_previo_dias) {
      const d = new Date();
      d.setDate(d.getDate() + convenio.aviso_previo_dias);
      setFechaEfectiva(d.toISOString().slice(0, 10));
    }
    // eslint-disable-next-line
  }, [convenio.aviso_previo_dias]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="no-renov-modal">
      <div className="bg-white w-full max-w-lg p-6 sm:p-8 border-2 border-amber-500 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="section-marker text-amber-700 flex items-center gap-1"><AlertTriangle size={12} strokeWidth={1.5} /> {t("cd_x.nr_critical")}</div>
            <h2 className="text-3xl font-medium tracking-tight mt-1">{t("cd_x.nr_modal_title")}</h2>
            <p className="text-sm text-zinc-600 mt-2">
              {t("cd_x.nr_modal_desc")} <strong>{convenio.aviso_previo_dias} {t("convenio_detail.days")}</strong>.
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-950"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("cd_x.nr_modal_date")}</label>
            <input required type="date" value={fechaEfectiva} onChange={(e) => setFechaEfectiva(e.target.value)} className="w-full border border-zinc-300 px-4 py-3" data-testid="nr-fecha" />
            <p className="text-xs text-zinc-500 mt-1">{t("cd_x.nr_modal_date_hint")}</p>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("cd_x.nr_modal_reason")}</label>
            <textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full border border-zinc-300 px-4 py-3 resize-none" data-testid="nr-motivo" />
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50">{t("common.cancel")}</button>
            <button type="submit" disabled={saving} className="flex-1 bg-amber-600 text-white px-6 py-3 font-medium hover:bg-amber-700 disabled:opacity-50" data-testid="nr-submit">
              {saving ? t("cd_x.nr_modal_processing") : t("cd_x.nr_modal_confirm")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
